"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Not a bill",
    rule: "An Expense is direct spend: Dr Expense Account, Cr Paid Through. No item lines, no GST on lines, no due date, no vendor Current Balance, no stock",
  },
  {
    topic: "Ledger posts on Save",
    rule: "Create posts immediately. Edit re-posts if amount, expense account, Paid Through, or date change. Delete reverses the journal, then removes the record",
  },
  {
    topic: "No statuses / no Mark As",
    rule: "There is no Draft, Open, or Cancelled. Edit and Delete follow permission only",
  },
  {
    topic: "Subscription gate",
    rule: "Creating an expense is limited by your plan. Editing and deleting are not separately gated",
  },
  {
    topic: "No primary-bank gate",
    rule: "You must pick Paid Through (a money/payment account). You do not need a primary bank",
  },
  {
    topic: "Expense number",
    rule: "Auto-generated with an EX prefix unless you turn that off under Expense Configuration on the form. That same drawer stores Terms & Conditions. Locked after save when auto-generate is on",
  },
  {
    topic: "Division, Branch, Warehouse",
    rule: "Required, plus Date, Expense Account, Amount, Paid Through, and organization shipping address. Customer/Vendor/Ledger Name is optional",
  },
  {
    topic: "GST",
    rule: "No item GST. Company GSTIN is stamped for display on the PDF and detail only. Amount is a flat figure",
  },
  {
    topic: "No Duplicate, no list Export, no Convert",
    rule: "Recurring Expense is a separate module — nothing converts an Expense into a schedule",
  },
  {
    topic: "Attachments",
    rule: "Upload Your Files — receipts, up to 3 files, 5 MB each",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating an expense" },
      { heading: "2. Expense vs Bill" },
      { heading: "3. Viewing, searching, and the list" },
      { heading: "4. Editing an expense" },
      { heading: "5. Printing and downloading" },
      { heading: "6. Deleting an expense" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function ExpensesPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Expenses
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone recording a paid cost that is not a vendor bill —
            fuel, travel, a one-off service
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            An <B>Expense</B> posts as soon as you <B>Save</B>: debit the{" "}
            <B>Expense Account</B>, credit <B>Paid Through</B>. There is no
            status workflow, no Mark As, and no warehouse movement. If you
            need items, GST lines, and a payable to a vendor, use a{" "}
            <B>Bill</B> instead.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating an expense
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick Division, Branch, Warehouse. Only <B>Save</B>.
          </p>
          <Definitions
            items={[
              {
                term: "Expenses#",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Expense Configuration</B>. Default prefix <B>EX</B>.
                  </>
                ),
              },
              {
                term: "Customer/Vendor/Ledger Name",
                desc: (
                  <>
                    Optional. Customers and account ledgers. This is not a
                    required vendor like a Bill.
                  </>
                ),
              },
              {
                term: "Expense Account",
                desc: (
                  <>
                    Required. Chart of accounts: Expense, COGS, or Other
                    Expense.
                  </>
                ),
              },
              {
                term: "Amount and Date",
                desc: <>Flat amount. Date is the posting date.</>,
              },
              {
                term: "Paid Through",
                desc: (
                  <>
                    Required. Money / payment account categories (same family
                    as Payments Made Paid Through).
                  </>
                ),
              },
              {
                term: "Notes, Terms, receipts",
                desc: (
                  <>
                    Notes and Terms &amp; Conditions (from the configuration
                    drawer). <B>Upload Your Files</B> for receipts.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Add Expense with Expense Account, Amount, Paid Through, and Save." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Expense vs Bill
          </h3>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    {" "}
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Expense
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Bill
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#F5F5F5]">
                  <td className="px-5 py-3 text-[14px] geist-medium text-[#111827]">
                    Nature
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    Already paid from Paid Through
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    Payable to a vendor
                  </td>
                </tr>
                <tr className="border-b border-[#F5F5F5]">
                  <td className="px-5 py-3 text-[14px] geist-medium text-[#111827]">
                    Items / GST lines
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    No
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    Yes
                  </td>
                </tr>
                <tr className="border-b border-[#F5F5F5]">
                  <td className="px-5 py-3 text-[14px] geist-medium text-[#111827]">
                    Status
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    None
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    Draft → Open / Paid / …
                  </td>
                </tr>
                <tr>
                  <td className="px-5 py-3 text-[14px] geist-medium text-[#111827]">
                    Stock
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    Never
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    Per Inventory Config
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <Callout type="note">
            Recurring Expenses generate live expenses (money moves on create).
            Recurring Bills generate Draft bills.
          </Callout>

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Viewing, searching, and the list
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Add. Preferences → Expense Configuration. No Excel export. Detail
            tabs: Basic Info, History, Attachments when receipts exist.
            Download and Print.
          </p>
          <Screenshot caption="Screenshot: Expenses list with Add and Preferences." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Editing an expense
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Allowed whenever you have Edit. Changing amount, expense account,
            Paid Through, or date deletes the old journal and posts a new
            one.
          </p>

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Printing and downloading
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Detail: <B>Download</B> and <B>Print</B>. GSTIN on the PDF is the
            company stamp, not line tax.
          </p>

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Deleting an expense
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Reverses the journal and removes receipts from storage. There is
            no Cancel status to keep a voided copy.
          </p>
          <Screenshot caption="Screenshot: Delete on an expense detail." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/purchase/bills"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Bills <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Use a bill when you still owe a vendor
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/recurring-expenses"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Recurring Expenses <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Schedule that creates these expenses automatically
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/payments-made"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Payments Made <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Paying a bill is a different module — Approve, not instant Save
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
